import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Button, Intent, Spinner, SpinnerSize } from '../../components/ui';
import { useAccount } from '../../hooks/useAccount';
import DefaultPageLayout from '../../layouts/defaultPageLayout';
import { Account } from '../../services/models/account';
import { BillingRoute } from '../../utils/Routes';

// The subscription only shows up once Stripe's webhook reaches the backend —
// usually within seconds of the checkout redirect. Poll with backoff instead
// of a single refetch, then surface a real error state rather than spinning
// forever (the session id in the callback URL may also simply be invalid).
export const POLL_DELAYS_MS = [2000, 4000, 8000, 16000];

// A planId alone doesn't prove the checkout succeeded: a cancel-at-period-end
// subscription keeps its old planId (with canceled=true) until the period
// ends, and pricing/paywall send exactly those users back through Checkout.
// Require a non-canceled subscription so the first poll can't mistake the
// stale one for the webhook's result.
const isPaymentCustomer = (account?: Account) =>
  !!account?.subscription?.planId && !account?.subscription?.canceled;

const StripeCheckoutCallbackPage: FunctionComponent = () => {
  const history = useHistory();
  const location = useLocation();

  const { account, refetch } = useAccount();

  // Stripe only appends the session id to success_url (newslit-webapp#918),
  // so an arrival without it is an abandoned checkout — no webhook is coming.
  const isAbandon = !new URLSearchParams(location.search).get('session_id');

  // Number of refetches performed so far; one immediate, then one per delay.
  const [attempt, setAttempt] = useState(0);

  const subscriptionActive = isPaymentCustomer(account);
  const timedOut = attempt > POLL_DELAYS_MS.length;

  useEffect(() => {
    if (isAbandon) {
      history.replace(BillingRoute);
    }
  }, [isAbandon, history]);

  const closeWindow = useCallback(() => {
    if (window.opener && window.opener.popupCompleted) {
      window.opener.popupCompleted();
    } else {
      history.push('/');
    }
  }, [history]);

  useEffect(() => {
    if (subscriptionActive) {
      closeWindow();
    }
  }, [subscriptionActive, closeWindow]);

  useEffect(() => {
    if (isAbandon || subscriptionActive || timedOut) {
      return;
    }

    // Cancelled on cleanup so an in-flight refetch can't advance the
    // counter after unmount or after the effect re-runs mid-poll.
    let cancelled = false;

    // Poll failures (e.g. a transient network error) just consume the
    // attempt; the timeout state is the terminal answer either way.
    const poll = () =>
      refetch()
        .catch(() => {})
        .then(() => {
          if (!cancelled) {
            setAttempt(a => a + 1);
          }
        });

    if (attempt === 0) {
      poll();
      return () => {
        cancelled = true;
      };
    }

    const timer = window.setTimeout(poll, POLL_DELAYS_MS[attempt - 1]);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [attempt, isAbandon, subscriptionActive, timedOut, refetch]);

  return (
    <DefaultPageLayout title="Verify Subscription">
      <div className="min-h-full flex flex-col justify-center py-12 bg-gray-50 sm:px-6 lg:px-8">
        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow-sm sm:rounded-lg sm:px-10">
            {timedOut ? (
              <>
                <h2 className="text-lg leading-6 font-medium text-gray-900">
                  We couldn't confirm your subscription
                </h2>
                <p className="mt-2 text-sm text-gray-500">
                  Your payment may still be processing. Check again in a moment — if you were
                  charged, your subscription will show up on your plan page shortly.
                </p>
                <Button
                  variant="primary"
                  className="mt-4 w-full flex"
                  onClick={() => setAttempt(0)}
                >
                  Check again
                </Button>
                <Button as={Link} to={BillingRoute} className="mt-2 w-full flex">
                  Back to your plan
                </Button>
              </>
            ) : (
              <>
                <h2 className="text-lg leading-6 font-medium text-gray-900">
                  <Spinner intent={Intent.PRIMARY} size={SpinnerSize.STANDARD} />
                  Checking your account status
                </h2>
                {/* Marker-less abandons bounce above; this covers stragglers
                    (e.g. a stale or hand-edited session id) so nobody has to
                    sit through the full poll to get an exit. */}
                <p className="mt-3 text-sm text-gray-500">
                  Cancelled your checkout?{' '}
                  <Link
                    to={BillingRoute}
                    className="font-medium text-secondary hover:text-secondary-dark"
                  >
                    Back to your plan
                  </Link>
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </DefaultPageLayout>
  );
};

export default StripeCheckoutCallbackPage;
